import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

import { AppStateService } from "../app-state.service";

type CorruptFeatureState =
  | string
  | number
  | boolean
  | Record<string, unknown>
  | unknown[]
  | null
  | undefined;

/**
 * CorruptFeaturesService
 * --------------------------------------------------------------------
 * Owns the reactive bookkeeping for "corrupt" features on the current
 * device (features whose memory-bank read failed and need default
 * values, or are unsupported and should be surfaced/filtered on the UI).
 *
 * Public field/method names mirror the legacy `ApiService` API so
 * consumers do not need to change.
 */
@Injectable({ providedIn: "root" })
export class CorruptFeaturesService {
  readonly corruptFeat = new BehaviorSubject<CorruptFeatureState>("");
  readonly readDataValuesFeature = new BehaviorSubject<CorruptFeatureState>("");
  readonly nonCorrupt = new BehaviorSubject<CorruptFeatureState>("");
  readonly redFeatures = new BehaviorSubject<CorruptFeatureState>("");
  readonly getRedFeature: Observable<CorruptFeatureState> = this.redFeatures.asObservable();

  private readonly corruptFeatures: { featureName: string; memoryBankId: string }[] = [];
  private readonly filteredNotSupportedCorruptFeatures$ = new BehaviorSubject<string[]>([]);
  private readonly corruptedFeaturesNeedingDefaults$ = new BehaviorSubject<string[]>([]);

  constructor(private appState: AppStateService) {}

  /** Append a corrupt-feature entry and mirror it into `AppStateService`. */
  pushCorruptFeature(featureName: string, memoryBankId: string): void {
    this.corruptFeatures.push({ featureName, memoryBankId });
    this.appState.setCorruptMemoryBankId(this.corruptFeatures);
  }

  setFilteredNotSupportedCorruptFeatures(features: string[]): void {
    this.filteredNotSupportedCorruptFeatures$.next(features);
  }

  getFilteredNotSupportedCorruptFeatures(): Observable<string[]> {
    return this.filteredNotSupportedCorruptFeatures$.asObservable();
  }

  setCorruptedFeaturesNeedingDefaults(features: string[]): void {
    this.corruptedFeaturesNeedingDefaults$.next(features);
  }

  getCorruptedFeaturesNeedingDefaults(): string[] {
    return this.corruptedFeaturesNeedingDefaults$.value;
  }

  clearCorruptedFeaturesNeedingDefaults(): void {
    this.corruptedFeaturesNeedingDefaults$.next([]);
  }

  sendCorruptFeat(data: CorruptFeatureState): void {
    this.corruptFeat.next(data);
  }
  sendNonCorrupt(data: CorruptFeatureState): void {
    this.nonCorrupt.next(data);
  }
  redFailedFeatures(data: CorruptFeatureState): void {
    this.redFeatures.next(data);
  }
  sendConfigForReader(data: CorruptFeatureState): void {
    this.readDataValuesFeature.next(data);
  }
}
